import {
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import * as React from "react";
import { useGo } from "@refinedev/core";
import RateReviewIcon from "@mui/icons-material/RateReview";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TranslateIcon from "@mui/icons-material/Translate";
import { AxiosError } from "axios";
import { getClientCounts } from "../../api/batches";
import {
  inviteCopywriter,
  listCopywriters,
  disableCopywriter,
  enableCopywriter,
  resendCopywriterInvite,
  type Copywriter,
} from "../../api/client";
import { useAuthStore } from "../../authProvider";
import { OrganizationUsage } from "../authorization/organization-usage";
import DocumentTranslation from "../authorization/document-translation";

interface ProfileClientProps {
  canCreate?: boolean;
}

export default function ProfileClient({ canCreate }: ProfileClientProps) {
  const { user } = useAuthStore();

  const isCopywriterClient = user?.isCopywriterClient;

  const canReview = user?.isClient;

  const go = useGo();

  // Client counts state
  const [clientCounts, setClientCounts] = React.useState<{
    to_check: number;
    to_rework: number;
  } | null>(null);

  // Fetch client counts on mount
  React.useEffect(() => {
    const fetchCounts = async () => {
      try {
        const response = await getClientCounts();
        setClientCounts(response.data.data);
      } catch (error) {
        console.error("Failed to fetch client counts:", error);
      }
    };

    fetchCounts();
  }, []);

  // Copywriters state
  const [copywriters, setCopywriters] = React.useState<Copywriter[]>([]);
  const [loadingCopywriters, setLoadingCopywriters] = React.useState(false);

  const [inviteForm, setInviteForm] = React.useState({
    name: "",
    surname: "",
    email: "",
  });

  const [openInviteModal, setOpenInviteModal] = React.useState(false);
  const [inviting, setInviting] = React.useState(false);
  const [inviteError, setInviteError] = React.useState("");
  const [resendingId, setResendingId] = React.useState<string | null>(null);
  const [showResendDialog, setShowResendDialog] = React.useState(false);
  const [resendCredentials, setResendCredentials] = React.useState<{
    name: string;
    email: string;
    password: string;
  } | null>(null);
  const [copySnackbar, setCopySnackbar] = React.useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = React.useState(false);
  const [copywriterToDisable, setCopywriterToDisable] = React.useState<
    string | null
  >(null);
  const [errorSnackbar, setErrorSnackbar] = React.useState<string | null>(null);
  const [usageRefreshKey, setUsageRefreshKey] = React.useState(0);

  // Fetch copywriters on mount
  React.useEffect(() => {
    const fetchCopywriters = async () => {
      try {
        setLoadingCopywriters(true);
        const response = await listCopywriters();
        setCopywriters(response.data.data);
      } catch (error) {
        console.error("Failed to fetch copywriters:", error);
      } finally {
        setLoadingCopywriters(false);
      }
    };

    fetchCopywriters();
  }, []);

  const handleInvite = async () => {
    if (inviteForm.name && inviteForm.surname && inviteForm.email) {
      try {
        setInviting(true);
        setInviteError("");
        const response = await inviteCopywriter(inviteForm);
        setCopywriters([response.data.data, ...copywriters]);
        setInviteForm({ name: "", surname: "", email: "" });
        setOpenInviteModal(false);
      } catch (error) {
        console.error("Failed to invite copywriter:", error);

        let errorMessage = "Failed to invite copywriter. Please try again.";
        if (error instanceof AxiosError && error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }

        setInviteError(errorMessage);
      } finally {
        setInviting(false);
      }
    }
  };

  const handleDisableCopywriter = (id: string) => {
    setCopywriterToDisable(id);
    setShowDisableConfirm(true);
  };

  const confirmDisableCopywriter = async () => {
    if (!copywriterToDisable) return;

    try {
      await disableCopywriter(copywriterToDisable);
      // Update the copywriter in the list to show as disabled
      setCopywriters(
        copywriters.map((c) =>
          c.id === copywriterToDisable ? { ...c, disabled: true } : c
        )
      );
      setShowDisableConfirm(false);
      setCopywriterToDisable(null);
    } catch (error) {
      console.error("Failed to disable copywriter:", error);
      setErrorSnackbar("Failed to disable copywriter. Please try again.");
      setShowDisableConfirm(false);
      setCopywriterToDisable(null);
    }
  };

  const handleEnableCopywriter = async (id: string) => {
    try {
      await enableCopywriter(id);
      // Update the copywriter in the list to show as enabled
      setCopywriters(
        copywriters.map((c) => (c.id === id ? { ...c, disabled: false } : c))
      );
    } catch (error) {
      console.error("Failed to enable copywriter:", error);
      setErrorSnackbar("Failed to enable copywriter. Please try again.");
    }
  };

  const handleResendInvite = async (id: string) => {
    try {
      setResendingId(id);
      const response = await resendCopywriterInvite(id);
      const { temporary_password, email, name, surname } = response.data.data;

      setResendCredentials({
        name: `${name} ${surname}`,
        email,
        password: temporary_password,
      });
      setShowResendDialog(true);
    } catch (error) {
      console.error("Failed to resend invite:", error);
      let errorMessage = "Failed to resend invite. Please try again.";
      if (error instanceof AxiosError && error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert(errorMessage);
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySnackbar(true);
  };

  return (
    <>
      {/* Navigation Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {canCreate && user?.isClient && (
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardActionArea onClick={() => go({ to: "/brands" })}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CreateIcon sx={{ fontSize: 32, color: "primary.main" }} />
                    <Box>
                      <Typography variant="h6">Create</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create and edit your work
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
        {canReview && (
          <Grid item xs={12} md={canCreate ? 6 : 12}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardActionArea onClick={() => go({ to: "/review" })}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <RateReviewIcon
                        sx={{ fontSize: 32, color: "primary.main" }}
                      />
                      <Box>
                        <Typography variant="h6">Review</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Verify Fashion Words' work
                        </Typography>
                      </Box>
                    </Box>
                    {clientCounts ? (
                      clientCounts.to_check > 0 ? (
                        <Chip
                          label={`${clientCounts.to_check} ${
                            clientCounts.to_check === 1 ? "job" : "jobs"
                          } to go`}
                          color="primary"
                          sx={{
                            backgroundColor: "#B00119",
                            color: "#FFFFFF",
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        <Chip
                          label="No reviews to do"
                          color="success"
                          sx={{
                            fontWeight: 500,
                          }}
                        />
                      )
                    ) : null}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
        {canCreate && isCopywriterClient && (
          <Grid item xs={12} md={12}>
            <Card elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <CardActionArea onClick={() => go({ to: "/brands" })}>
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <RateReviewIcon
                        sx={{ fontSize: 32, color: "primary.main" }}
                      />
                      <Box>
                        <Typography variant="h6">Your works</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        )}
      </Grid>

      {user?.organization_id && user?.mode_allow_translate_ai && (
        <Card elevation={0} sx={{ border: "1px solid #e0e0e0", mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TranslateIcon sx={{ fontSize: 32, color: "primary.main" }} />
                <Box>
                  <Typography variant="h6">Translate Document</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Translate PDF and DOCX files with AI
                  </Typography>
                </Box>
              </Box>
              <DocumentTranslation compact onClose={() => setUsageRefreshKey(k => k + 1)} />
            </Box>
          </CardContent>
        </Card>
      )}

      {user?.organization_id && user?.mode_allow_translate_ai && (
        <OrganizationUsage key={usageRefreshKey} organizationId={user.organization_id} />
      )}

      {/* Copywriters Table */}
      {canCreate && user?.isClient && (
        <>
          <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", mb: 3 }}>
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h6">Copywriters</Typography>
              <IconButton
                color="primary"
                size="small"
                onClick={() => setOpenInviteModal(true)}
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Surname</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCopywriters ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : copywriters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No copywriters invited yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    copywriters.map((copywriter) => (
                      <TableRow
                        key={copywriter.id}
                        sx={{
                          opacity: copywriter.disabled ? 0.6 : 1,
                          backgroundColor: copywriter.disabled
                            ? "#fafafa"
                            : "inherit",
                        }}
                      >
                        <TableCell>{copywriter.name}</TableCell>
                        <TableCell>{copywriter.surname}</TableCell>
                        <TableCell>{copywriter.email}</TableCell>
                        <TableCell>
                          {copywriter.disabled ? (
                            <Chip label="Disabled" size="small" color="error" />
                          ) : (
                            <Chip
                              label={copywriter.status}
                              size="small"
                              color={
                                copywriter.status === "Joined"
                                  ? "success"
                                  : "warning"
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            {/* Resend invite button - only for pending, non-disabled copywriters */}
                            {!copywriter.email_confirmed &&
                              !copywriter.disabled && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() =>
                                    handleResendInvite(copywriter.id)
                                  }
                                  disabled={resendingId === copywriter.id}
                                >
                                  {resendingId === copywriter.id
                                    ? "Sending..."
                                    : "Resend invite"}
                                </Button>
                              )}
                            {copywriter.disabled ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() =>
                                  handleEnableCopywriter(copywriter.id)
                                }
                              >
                                Enable
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() =>
                                  handleDisableCopywriter(copywriter.id)
                                }
                              >
                                Disable
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Invite Modal */}
          <Dialog
            open={openInviteModal}
            onClose={() => {
              setOpenInviteModal(false);
              setInviteError("");
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Invite Copywriter</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={inviteForm.name}
                      onChange={(e) =>
                        setInviteForm({ ...inviteForm, name: e.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Surname"
                      value={inviteForm.surname}
                      onChange={(e) =>
                        setInviteForm({
                          ...inviteForm,
                          surname: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) => {
                        setInviteForm({ ...inviteForm, email: e.target.value });
                        setInviteError("");
                      }}
                      error={!!inviteError}
                      helperText={inviteError}
                    />
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenInviteModal(false);
                  setInviteError("");
                }}
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleInvite}
                disabled={inviting}
              >
                {inviting ? "Sending..." : "Send Invite"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Resend Credentials Dialog */}
      <Dialog
        open={showResendDialog}
        onClose={() => setShowResendDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invitation Resent</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            In case of problems with the email, you can copy and paste the
            credentials
          </Typography>

          <Box
            sx={{
              position: "relative",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              backgroundColor: "#f5f5f5",
              p: 2,
              pr: 6,
            }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{ m: 0, fontFamily: "monospace" }}
            >
              {`Name: ${resendCredentials?.name || ""}\nEmail: ${
                resendCredentials?.email || ""
              }\nPassword: ${resendCredentials?.password || ""}`}
            </Typography>
            <IconButton
              onClick={() =>
                handleCopyToClipboard(
                  `Name: ${resendCredentials?.name || ""}\nEmail: ${
                    resendCredentials?.email || ""
                  }\nPassword: ${resendCredentials?.password || ""}`
                )
              }
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResendDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Copied to clipboard"
      />

      {/* Error snackbar */}
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={3000}
        onClose={() => setErrorSnackbar(null)}
        message={errorSnackbar}
      />

      {/* Disable Confirmation Dialog */}
      <Dialog
        open={showDisableConfirm}
        onClose={() => {
          setShowDisableConfirm(false);
          setCopywriterToDisable(null);
        }}
      >
        <DialogTitle>Disable Copywriter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to disable this copywriter? They will not be
            able to log in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDisableConfirm(false);
              setCopywriterToDisable(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDisableCopywriter}
            color="error"
            variant="contained"
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
